import {
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  startAfter,
  updateDoc,
  where,
  type DocumentData,
  type DocumentSnapshot,
  type QueryDocumentSnapshot,
  type Transaction as FirestoreTransaction
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from './config';
import { accountLedgerCol, accountsCol, balanceAuditsCol, budgetsCol, contactsCol, crdrCol, mastersCol, monthlyPeriodDoc, monthlyPeriodEventsCol, monthlyPeriodsCol, notificationDeliveriesCol, notificationDevicesCol, notificationSettingsDoc, notificationsCol, paymentsCol, plansCol, summariesCol, transactionsCol, userDoc } from './paths';
import { addMonths, displayDate, monthKey, nextDueDate, normalizeDateISO, nowISO, todayISO } from '../utils/date';
import { contactDuplicateKeys, normalizeContactInput } from '../utils/contacts';
import { DEFAULT_DROPDOWNS, DROPDOWN_GROUP_OPTIONS, DROPDOWN_GROUPS, defaultDropdownValue, normalizeDropdownValue, type DropdownGroup } from '../types/dropdowns';
import type { Account, AccountLedgerEntry, AccountType, AppNotification, BalanceAudit, Budget, Contact, CrDrEntry, Flow, MasterItem, MonthlyPeriod, MonthlyPeriodCloseSummary, MonthlyPeriodEvent, MonthlySummary, NotificationDelivery, NotificationDevice, NotificationPreferences, Plan, PlanFrequency, PlanPayment, Transaction } from '../types';
import { normalizeNotificationPreferences } from '../utils/notificationDelivery';
import { isViteDevRuntime, type RuntimeImportMeta } from '../utils/runtimeEnv';
import { assertFinancialDateMutableStatus, assertFinancialMutationMutableStatus, canClosePeriod, canMarkReviewed, canReopenPeriod, defaultMonthlyPeriod, normalizeMonthlyPeriodStatus, resolveFinancialMonthKey } from '../utils/monthLocks';
import {
  applyLedgerDelta,
  assertFreshLedgerRows,
  buildLedgerEditAudit,
  cardPaymentAccountDeltas,
  normalizeCardPaymentEdit,
  normalizeTransferEdit,
  transferAccountDeltas,
  type CardPaymentEditPatch,
  type TransferEditPatch
} from '../utils/linkedLedgerMutation';
import {
  buildTransactionEditAudit,
  canEditTransaction,
  financialImpact,
  normalizeEditableTransaction,
  reverseImpact,
  assertFreshTransactionVersion,
  assertActiveAccountForMutation,
  type EditableTransactionPatch,
  type TransactionImpact
} from '../utils/transactionMutation';

export const defaultMasters: Array<Pick<MasterItem, 'type' | 'group' | 'value' | 'normalizedValue' | 'sortOrder' | 'active'>> = DROPDOWN_GROUP_OPTIONS.flatMap((group) =>
  DEFAULT_DROPDOWNS[group].map((value, i) => ({ type: group, group, value, normalizedValue: normalizeDropdownValue(value), sortOrder: i + 1, active: true }))
);

const debugEnabled = isViteDevRuntime(import.meta as unknown as RuntimeImportMeta);

const debugFirestore = (label: string, data: Record<string, unknown>) => {
  if (debugEnabled) console.debug(`[PFS Firestore] ${label}`, data);
};

const debugDropdowns = (label: string, data: Record<string, unknown>) => {
  if (debugEnabled) console.debug(`[PFS Dropdowns] ${label}`, data);
};

const debugPlans = (label: string, data: Record<string, unknown>) => {
  if (debugEnabled) console.debug(`[PFS Plans] ${label}`, data);
};

const debugReports = (label: string, data: Record<string, unknown>) => {
  if (debugEnabled) console.debug(`[PFS Reports] ${label}`, data);
};

const debugBudgets = (label: string, data: Record<string, unknown>) => {
  if (debugEnabled) console.debug(`[PFS Budgets] ${label}`, data);
};

const debugAccounts = (label: string, data: Record<string, unknown>) => {
  if (debugEnabled) console.debug(`[PFS Accounts] ${label}`, data);
};

const withId = <T>(snap: QueryDocumentSnapshot<DocumentData>) => ({ id: snap.id, ...snap.data() }) as T;

const isDropdownGroup = (value: unknown): value is DropdownGroup =>
  typeof value === 'string' && (DROPDOWN_GROUP_OPTIONS as string[]).includes(value);

const normalizeMaster = (row: Partial<MasterItem> & { id: string }) => {
  const groupCandidate = row.group || row.type;
  const group = isDropdownGroup(groupCandidate) ? groupCandidate : DROPDOWN_GROUPS.DAILY_CATEGORY;
  const value = String(row.value || '').trim();
  return {
    ...row,
    uid: String(row.uid || ''),
    type: group,
    group,
    value,
    normalizedValue: row.normalizedValue || normalizeDropdownValue(value),
    active: row.active !== false,
    sortOrder: Number(row.sortOrder || 999)
  } satisfies MasterItem;
};

const normalizeTransaction = (row: Transaction) => {
  const dateISO = normalizeDateISO(row.dateISO || row.date);
  return {
    ...row,
    date: dateISO,
    dateISO,
    displayDate: row.displayDate || displayDate(dateISO),
    monthKey: row.monthKey && /^\d{4}-\d{2}$/.test(row.monthKey) ? row.monthKey : monthKey(dateISO),
    amount: Number(row.amount || 0),
    type: row.type || 'Expense',
    category: row.category || defaultDropdownValue(DROPDOWN_GROUPS.DAILY_CATEGORY),
    paymentMode: row.paymentMode || defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE),
    taxTag: row.taxTag || '',
    taxCategory: row.taxCategory || '',
    reportGroup: row.reportGroup || '',
    isTaxRelevant: row.isTaxRelevant ?? Boolean(row.taxTag && row.taxTag !== 'Ignore for ITR'),
    status: row.status || 'active',
    active: row.active ?? row.status !== 'deleted'
  } satisfies Transaction;
};

const normalizeContact = (row: Contact) => {
  const normalized = normalizeContactInput(row);
  return {
    ...row,
    ...normalized,
    uid: row.uid || '',
    active: row.active ?? true
  } satisfies Contact;
};

const normalizePlanStatus = (status?: string) => {
  const normalized = String(status || 'active').toLowerCase();
  if (normalized === 'done') return 'done';
  if (normalized === 'deleted') return 'deleted';
  if (normalized === 'paused') return 'paused';
  if (normalized === 'pending') return 'active';
  return 'active';
};

const normalizePlan = (row: Plan) => {
  const dueDate = normalizeDateISO(row.nextDueDate || row.dueDate);
  const status = normalizePlanStatus(String(row.status || 'active'));
  return {
    ...row,
    dueDate,
    nextDueDate: row.nextDueDate || dueDate,
    monthKey: row.monthKey && /^\d{4}-\d{2}$/.test(row.monthKey) ? row.monthKey : monthKey(dueDate),
    amount: Number(row.amount || 0),
    paidCount: Number(row.paidCount || 0),
    totalCount: Number(row.totalCount || 0),
    status,
    active: row.active ?? status === 'active',
    paymentMode: row.paymentMode || defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE),
    paidFromAccountId: row.paidFromAccountId || '',
    paidFromAccountName: row.paidFromAccountName || '',
    paidFromAccountType: row.paidFromAccountType,
    calendarSyncEnabled: row.calendarSyncEnabled ?? false,
    reminderDaysBefore: Number(row.reminderDaysBefore ?? 3),
    title: row.title || 'Untitled plan',
    category: row.category || defaultDropdownValue(DROPDOWN_GROUPS.PLAN_CATEGORY),
    frequency: (row.frequency || defaultDropdownValue(DROPDOWN_GROUPS.PLAN_FREQUENCY)) as PlanFrequency,
    note: row.note || ''
  } satisfies Plan;
};

const normalizePlanPayment = (row: PlanPayment) => {
  const paidDate = normalizeDateISO(row.paidDate);
  return {
    ...row,
    paidDate,
    monthKey: row.monthKey && /^\d{4}-\d{2}$/.test(row.monthKey) ? row.monthKey : monthKey(paidDate),
    amount: Number(row.amount || 0),
    title: row.title || row.planName || 'Paid plan',
    paymentMode: row.paymentMode || defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE),
    note: row.note || row.remark || ''
  } satisfies PlanPayment;
};

const normalizeCrDr = (row: CrDrEntry) => {
  const date = normalizeDateISO(row.date);
  return {
    ...row,
    date,
    monthKey: row.monthKey && /^\d{4}-\d{2}$/.test(row.monthKey) ? row.monthKey : monthKey(date),
    name: row.name || row.contactName || '',
    amount: Number(row.amount || 0),
    type: row.type || 'DR',
    status: row.status || 'Open'
  } satisfies CrDrEntry;
};

const normalizeBudget = (row: Budget) => ({
  ...row,
  monthKey: row.monthKey && /^\d{4}-\d{2}$/.test(row.monthKey) ? row.monthKey : monthKey(todayISO()),
  category: String(row.category || '').trim(),
  limitAmount: Number(row.limitAmount || 0),
  active: row.active ?? row.status !== 'deleted',
  status: row.status || 'active'
}) satisfies Budget;

const normalizeAccount = (row: Account) => {
  const type = (row.type || 'bank') as AccountType;
  const currentBalance = Number(row.currentBalance ?? row.openingBalance ?? 0);
  const creditLimit = Number(row.creditLimit || 0);
  return {
    ...row,
    name: String(row.name || '').trim() || 'Unnamed account',
    type,
    institutionName: String(row.institutionName || '').trim(),
    last4: String(row.last4 || '').trim(),
    openingBalance: Number(row.openingBalance || 0),
    currentBalance,
    creditLimit,
    availableLimit: type === 'credit_card' ? Number(row.availableLimit ?? Math.max(creditLimit - currentBalance, 0)) : Number(row.availableLimit || 0),
    active: row.active ?? (row.status !== 'deleted' && row.status !== 'archived'),
    status: row.status || 'active'
  } satisfies Account;
};

const normalizeAccountLedger = (row: AccountLedgerEntry) => {
  const dateISO = normalizeDateISO(row.dateISO || row.date);
  return {
    ...row,
    date: dateISO,
    dateISO,
    monthKey: row.monthKey && /^\d{4}-\d{2}$/.test(row.monthKey) ? row.monthKey : monthKey(dateISO),
    amount: Number(row.amount || 0),
    type: row.type || 'expense',
    direction: row.direction || 'debit'
  } satisfies AccountLedgerEntry;
};

const normalizeBalanceAudit = (row: BalanceAudit) => {
  const dateISO = normalizeDateISO(row.dateISO || row.auditDate);
  return {
    ...row,
    auditDate: dateISO,
    dateISO,
    monthKey: row.monthKey && /^\d{4}-\d{2}$/.test(row.monthKey) ? row.monthKey : monthKey(dateISO),
    systemBalance: Number(row.systemBalance || 0),
    actualBalance: Number(row.actualBalance || 0),
    difference: Number(row.difference ?? Number(row.actualBalance || 0) - Number(row.systemBalance || 0)),
    creditLimit: Number(row.creditLimit || 0),
    systemAvailableLimit: Number(row.systemAvailableLimit || 0),
    actualAvailableLimit: Number(row.actualAvailableLimit || 0),
    availableLimitDifference: Number(row.availableLimitDifference || 0)
  } satisfies BalanceAudit;
};

const normalizeMonthlyPeriod = (row: Partial<MonthlyPeriod> & { id: string }, uid: string, key?: string) => ({
  ...row,
  id: row.id || key || row.monthKey || '',
  uid: row.uid || uid,
  monthKey: row.monthKey || key || row.id,
  status: normalizeMonthlyPeriodStatus(row.status),
  warningCountAtClose: Number(row.warningCountAtClose || 0),
  criticalCountAtClose: Number(row.criticalCountAtClose || 0),
  closeCount: Number(row.closeCount || 0)
}) satisfies MonthlyPeriod;

const monthlyPeriodFromSnap = (uid: string, key: string, snap: DocumentSnapshot<DocumentData>) =>
  snap.exists() ? normalizeMonthlyPeriod({ id: snap.id, ...snap.data() }, uid, key) : defaultMonthlyPeriod(uid, key);

const getMonthlyPeriodForTransaction = async (firestoreTx: FirestoreTransaction, uid: string, key: string) =>
  monthlyPeriodFromSnap(uid, key, await firestoreTx.get(monthlyPeriodDoc(uid, key)));

const assertFinancialDateMutableInTransaction = async (firestoreTx: FirestoreTransaction, uid: string, date: string) => {
  const key = resolveFinancialMonthKey(date);
  const period = await getMonthlyPeriodForTransaction(firestoreTx, uid, key);
  assertFinancialDateMutableStatus(date, period);
};

const assertFinancialMutationMutableInTransaction = async (firestoreTx: FirestoreTransaction, uid: string, originalDate: string, nextDate?: string) => {
  const keys = Array.from(new Set([resolveFinancialMonthKey(originalDate), resolveFinancialMonthKey(nextDate || originalDate)]));
  const periods = new Map<string, MonthlyPeriod>();
  for (const key of keys) {
    periods.set(key, await getMonthlyPeriodForTransaction(firestoreTx, uid, key));
  }
  assertFinancialMutationMutableStatus(originalDate, nextDate, periods);
};

const activeAccountFromSnap = (snap: DocumentSnapshot<DocumentData>) => {
  if (!snap.exists()) throw new Error('Selected account was not found.');
  const account = normalizeAccount({ id: snap.id, ...snap.data() } as Account);
  assertActiveAccountForMutation(account);
  return account;
};

const updateAccountImpact = (firestoreTx: FirestoreTransaction, account: Account, impact: TransactionImpact | null) => {
  if (!impact) return;
  const nextBalance = Number(account.currentBalance || 0) + impact.balanceDelta;
  firestoreTx.update(doc(accountsCol(account.uid), account.id), {
    currentBalance: nextBalance,
    ...(account.type === 'credit_card' ? { availableLimit: Math.max(Number(account.creditLimit || 0) - nextBalance, 0) } : {}),
    updatedAt: serverTimestamp()
  });
};

const updateAccountBalance = (firestoreTx: FirestoreTransaction, account: Account) => {
  firestoreTx.update(doc(accountsCol(account.uid), account.id), {
    currentBalance: Number(account.currentBalance || 0),
    ...(account.type === 'credit_card' ? { availableLimit: Math.max(Number(account.creditLimit || 0) - Number(account.currentBalance || 0), 0) } : {}),
    updatedAt: serverTimestamp()
  });
};

const periodSummaryPayload = (summary: MonthlyPeriodCloseSummary) => ({
  warningCountAtClose: Number(summary.warningCountAtClose || 0),
  criticalCountAtClose: Number(summary.criticalCountAtClose || 0),
  reconciliationSummary: summary.reconciliationSummary || {
    liquidClosingBalance: 0,
    cardOutstanding: 0,
    reconciledAccounts: 0,
    totalAccounts: 0,
    unresolvedWarnings: Number(summary.warningCountAtClose || 0)
  }
});

const monthlyPeriodEvent = (uid: string, key: string, eventType: MonthlyPeriodEvent['eventType'], summary?: MonthlyPeriodCloseSummary, reason?: string): Omit<MonthlyPeriodEvent, 'id'> => ({
  uid,
  monthKey: key,
  eventType,
  actorUid: uid,
  reason: reason || '',
  ...(summary ? { summary: periodSummaryPayload(summary) } : {}),
  createdAt: serverTimestamp(),
  createdAtISO: nowISO()
});

export async function fetchMonthlyPeriod(uid: string, key: string) {
  const snap = await getDoc(monthlyPeriodDoc(uid, key));
  return monthlyPeriodFromSnap(uid, key, snap);
}

export function listenMonthlyPeriod(uid: string, key: string, cb: (period: MonthlyPeriod) => void) {
  return onSnapshot(monthlyPeriodDoc(uid, key), (snap) => cb(monthlyPeriodFromSnap(uid, key, snap)));
}

export function listenMonthlyPeriodEvents(uid: string, key: string, cb: (events: MonthlyPeriodEvent[]) => void) {
  return onSnapshot(monthlyPeriodEventsCol(uid, key), (snap) => {
    const rows = snap.docs
      .map((item) => ({ id: item.id, ...item.data() }) as MonthlyPeriodEvent)
      .sort((a, b) => String(b.createdAtISO || '').localeCompare(String(a.createdAtISO || '')));
    cb(rows);
  });
}

export async function markMonthlyReviewComplete(uid: string, key: string, summary: MonthlyPeriodCloseSummary) {
  await runTransaction(db, async (tx) => {
    const periodRef = monthlyPeriodDoc(uid, key);
    const eventRef = doc(monthlyPeriodEventsCol(uid, key));
    const current = monthlyPeriodFromSnap(uid, key, await tx.get(periodRef));
    if (!canMarkReviewed(current.status)) throw new Error('Closed months must be reopened before marking review complete again.');
    const now = nowISO();
    tx.set(periodRef, {
      uid,
      monthKey: key,
      status: 'REVIEWED',
      ...periodSummaryPayload(summary),
      reviewedAt: serverTimestamp(),
      reviewedAtISO: now,
      reviewedBy: uid,
      createdAt: current.createdAt || serverTimestamp(),
      createdAtISO: current.createdAtISO || now,
      updatedAt: serverTimestamp(),
      updatedAtISO: now
    } satisfies Omit<MonthlyPeriod, 'id'>, { merge: true });
    tx.set(eventRef, monthlyPeriodEvent(uid, key, 'REVIEWED', summary));
  });
}

export async function closeMonthlyPeriod(uid: string, key: string, summary: MonthlyPeriodCloseSummary) {
  await runTransaction(db, async (tx) => {
    const periodRef = monthlyPeriodDoc(uid, key);
    const eventRef = doc(monthlyPeriodEventsCol(uid, key));
    const current = monthlyPeriodFromSnap(uid, key, await tx.get(periodRef));
    if (!canClosePeriod(current.status, summary)) {
      if (current.status === 'CLOSED') throw new Error('This month is already closed.');
      throw new Error('Resolve critical monthly close blockers before closing this period.');
    }
    const now = nowISO();
    const closeCount = Number(current.closeCount || 0) + 1;
    tx.set(periodRef, {
      uid,
      monthKey: key,
      status: 'CLOSED',
      ...periodSummaryPayload(summary),
      closedAt: serverTimestamp(),
      closedAtISO: now,
      closedBy: uid,
      ...(current.status === 'REOPENED' || closeCount > 1 ? { lastRelockedAt: serverTimestamp(), lastRelockedAtISO: now } : {}),
      closeCount,
      createdAt: current.createdAt || serverTimestamp(),
      createdAtISO: current.createdAtISO || now,
      updatedAt: serverTimestamp(),
      updatedAtISO: now
    } satisfies Omit<MonthlyPeriod, 'id'>, { merge: true });
    tx.set(eventRef, monthlyPeriodEvent(uid, key, closeCount > 1 ? 'RECLOSED' : 'CLOSED', summary));
  });
}

export async function reopenMonthlyPeriod(uid: string, key: string, reason: string) {
  const cleanReason = String(reason || '').trim();
  await runTransaction(db, async (tx) => {
    const periodRef = monthlyPeriodDoc(uid, key);
    const eventRef = doc(monthlyPeriodEventsCol(uid, key));
    const current = monthlyPeriodFromSnap(uid, key, await tx.get(periodRef));
    if (!canReopenPeriod(current.status, cleanReason)) throw new Error('Only closed months can be reopened, and a reason is required.');
    const now = nowISO();
    tx.set(periodRef, {
      uid,
      monthKey: key,
      status: 'REOPENED',
      reopenReason: cleanReason,
      reopenedAt: serverTimestamp(),
      reopenedAtISO: now,
      reopenedBy: uid,
      updatedAt: serverTimestamp(),
      updatedAtISO: now
    }, { merge: true });
    tx.set(eventRef, monthlyPeriodEvent(uid, key, 'REOPENED', undefined, cleanReason));
  });
}

export async function seedDefaultMasters(uid: string) {
  await setDoc(userDoc(uid), { uid, initializedAt: nowISO(), app: 'PFS' }, { merge: true });
  const existing = await getDocs(mastersCol(uid));
  const existingKeys = new Set(existing.docs.map((snap) => {
    const row = normalizeMaster(withId<MasterItem>(snap));
    return `${row.group}:${row.normalizedValue}`;
  }));
  const missing = defaultMasters.filter((m) => !existingKeys.has(`${m.group}:${m.normalizedValue}`));
  debugDropdowns('seedDefaultMasters', { uid, path: `users/${uid}/masters`, existing: existing.size, missing: missing.length });
  await Promise.all(missing.map((m) => addDoc(mastersCol(uid), { ...m, uid, createdAt: nowISO(), updatedAt: nowISO() })));
}

export function listenMasters(uid: string, cb: (items: MasterItem[]) => void) {
  const path = `users/${uid}/masters`;
  return onSnapshot(mastersCol(uid), (snap) => {
    const rows = snap.docs
      .map(withId<MasterItem>)
      .map(normalizeMaster)
      .filter((row) => row.value)
      .sort((a, b) => a.group.localeCompare(b.group) || a.sortOrder - b.sortOrder || a.value.localeCompare(b.value));
    debugDropdowns('listenMasters', { uid, path, fetched: snap.size, returned: rows.length, groups: [...new Set(rows.map((row) => row.group))] });
    cb(rows);
  });
}

export function listenContacts(uid: string, cb: (items: Contact[]) => void) {
  return onSnapshot(query(contactsCol(uid), orderBy('name')), (snap) => cb(snap.docs.map(withId<Contact>).map(normalizeContact)));
}

export async function saveMaster(uid: string, item: Partial<MasterItem>) {
  const groupCandidate = item.group || item.type;
  const group = isDropdownGroup(groupCandidate) ? groupCandidate : DROPDOWN_GROUPS.DAILY_CATEGORY;
  const value = String(item.value || '').trim();
  if (!value) throw new Error('Dropdown value is required.');
  const normalizedValue = normalizeDropdownValue(value);
  const existing = await getDocs(mastersCol(uid));
  const duplicate = existing.docs
    .map(withId<MasterItem>)
    .map(normalizeMaster)
    .find((row) => row.id !== item.id && row.group === group && row.normalizedValue === normalizedValue);
  if (duplicate) throw new Error(`"${value}" already exists in ${group}.`);
  const payload = { ...item, uid, type: group, group, value, normalizedValue, updatedAt: nowISO(), active: item.active ?? true, sortOrder: item.sortOrder ?? 999 };
  debugDropdowns('saveMaster', { uid, path: `users/${uid}/masters`, group, hasValue: Boolean(value), editing: Boolean(item.id) });
  if (item.id) return updateDoc(doc(mastersCol(uid), item.id), payload);
  return addDoc(mastersCol(uid), { ...payload, createdAt: nowISO() });
}

export const deleteMaster = (uid: string, id: string) => deleteDoc(doc(mastersCol(uid), id));

export async function saveContact(uid: string, item: Partial<Contact>) {
  const clean = normalizeContactInput(item);
  if (!clean.name && !clean.phone && !clean.email) throw new Error('Contact needs a name, phone, or email.');
  const keys = contactDuplicateKeys(clean);
  if (keys.length) {
    const existing = await getDocs(contactsCol(uid));
    const duplicate = existing.docs
      .map(withId<Contact>)
      .map(normalizeContact)
      .find((row) => row.id !== item.id && contactDuplicateKeys(row).some((key) => keys.includes(key)));
    if (duplicate) throw new Error(`${clean.name || clean.phone || clean.email} already exists in contacts.`);
  }
  const payload = { ...clean, uid, updatedAt: nowISO() };
  if (item.id) return updateDoc(doc(contactsCol(uid), item.id), payload);
  return addDoc(contactsCol(uid), { ...payload, createdAt: nowISO() });
}

export const deleteContact = (uid: string, id: string) => deleteDoc(doc(contactsCol(uid), id));

export function listenBudgets(uid: string, key: string, cb: (items: Budget[]) => void) {
  return onSnapshot(budgetsCol(uid), (snap) => {
    const rows = snap.docs
      .map(withId<Budget>)
      .map(normalizeBudget)
      .filter((row) => row.status !== 'deleted' && row.active !== false)
      .filter((row) => row.monthKey === key)
      .sort((a, b) => a.category.localeCompare(b.category));
    debugBudgets('listenBudgets', { uid, collection: `users/${uid}/budgets`, fetched: snap.size, month: key, returned: rows.length });
    cb(rows);
  });
}

export async function fetchBudgets(uid: string, key?: string) {
  const snap = await getDocs(budgetsCol(uid));
  const rows = snap.docs
    .map(withId<Budget>)
    .map(normalizeBudget)
    .filter((row) => row.status !== 'deleted' && row.active !== false)
    .filter((row) => !key || row.monthKey === key)
    .sort((a, b) => b.monthKey.localeCompare(a.monthKey) || a.category.localeCompare(b.category));
  debugBudgets('fetchBudgets', { uid, collection: `users/${uid}/budgets`, fetched: snap.size, month: key || '(all)', returned: rows.length });
  return rows;
}

export async function saveBudget(uid: string, input: Partial<Budget>) {
  const key = String(input.monthKey || '').trim();
  const category = String(input.category || '').trim();
  const limitAmount = Number(input.limitAmount || 0);
  if (!/^\d{4}-\d{2}$/.test(key)) throw new Error('Budget month is required.');
  if (!category) throw new Error('Budget category is required.');
  if (limitAmount <= 0) throw new Error('Budget limit must be greater than zero.');

  const existing = await getDocs(budgetsCol(uid));
  const duplicate = existing.docs
    .map(withId<Budget>)
    .map(normalizeBudget)
    .find((row) => row.id !== input.id && row.status !== 'deleted' && row.monthKey === key && row.category.toLowerCase() === category.toLowerCase());
  if (duplicate) throw new Error(`Budget already exists for ${category} in ${key}.`);

  const payload = { uid, monthKey: key, category, limitAmount, active: input.active ?? true, status: input.status || 'active', updatedAt: serverTimestamp() };
  debugBudgets('saveBudget', { uid, collection: `users/${uid}/budgets`, month: key, category, editing: Boolean(input.id) });
  if (input.id) return updateDoc(doc(budgetsCol(uid), input.id), payload);
  return addDoc(budgetsCol(uid), { ...payload, createdAt: serverTimestamp() });
}

export const deleteBudget = (uid: string, id: string) => updateDoc(doc(budgetsCol(uid), id), {
  status: 'deleted',
  active: false,
  deletedAt: serverTimestamp(),
  updatedAt: serverTimestamp()
});

export function listenAccounts(uid: string, cb: (items: Account[]) => void) {
  return onSnapshot(accountsCol(uid), (snap) => {
    const rows = snap.docs
      .map(withId<Account>)
      .map(normalizeAccount)
      .filter((row) => row.status !== 'deleted')
      .sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
    debugAccounts('listenAccounts', { uid, collection: `users/${uid}/accounts`, fetched: snap.size, returned: rows.length });
    cb(rows);
  });
}

export async function fetchAccounts(uid: string) {
  const snap = await getDocs(accountsCol(uid));
  const rows = snap.docs
    .map(withId<Account>)
    .map(normalizeAccount)
    .filter((row) => row.status !== 'deleted')
    .sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
  debugAccounts('fetchAccounts', { uid, collection: `users/${uid}/accounts`, fetched: snap.size, returned: rows.length });
  return rows;
}

export async function saveAccount(uid: string, input: Partial<Account>) {
  const name = String(input.name || '').trim();
  const type = (input.type || 'bank') as AccountType;
  const openingBalance = Number(input.openingBalance || 0);
  const currentBalance = Number(input.currentBalance ?? openingBalance);
  const creditLimit = Number(input.creditLimit || 0);
  if (!name) throw new Error('Account name is required.');
  const payload = {
    uid,
    name,
    type,
    institutionName: String(input.institutionName || '').trim(),
    last4: String(input.last4 || '').trim(),
    openingBalance,
    currentBalance,
    creditLimit,
    availableLimit: type === 'credit_card' ? Number(input.availableLimit ?? Math.max(creditLimit - currentBalance, 0)) : 0,
    active: input.active ?? true,
    status: input.status || 'active',
    updatedAt: serverTimestamp()
  };
  debugAccounts('saveAccount', { uid, collection: `users/${uid}/accounts`, type, editing: Boolean(input.id) });
  if (input.id) return updateDoc(doc(accountsCol(uid), input.id), payload);
  return addDoc(accountsCol(uid), { ...payload, createdAt: serverTimestamp() });
}

export const archiveAccount = (uid: string, id: string) => updateDoc(doc(accountsCol(uid), id), {
  status: 'archived',
  active: false,
  archivedAt: serverTimestamp(),
  updatedAt: serverTimestamp()
});

export const restoreAccount = (uid: string, id: string) => updateDoc(doc(accountsCol(uid), id), {
  status: 'active',
  active: true,
  updatedAt: serverTimestamp()
});

export const deleteAccount = (uid: string, id: string) => updateDoc(doc(accountsCol(uid), id), {
  status: 'deleted',
  active: false,
  deletedAt: serverTimestamp(),
  updatedAt: serverTimestamp()
});

export function listenAccountLedger(uid: string, accountId: string, key: string, cb: (items: AccountLedgerEntry[]) => void) {
  return onSnapshot(accountLedgerCol(uid), (snap) => {
    const rows = snap.docs
      .map(withId<AccountLedgerEntry>)
      .map(normalizeAccountLedger)
      .filter((row) => row.accountId === accountId)
      .filter((row) => !key || row.monthKey === key)
      .sort((a, b) => a.dateISO.localeCompare(b.dateISO));
    debugAccounts('listenAccountLedger', { uid, collection: `users/${uid}/accountLedger`, accountId, month: key, fetched: snap.size, returned: rows.length });
    cb(rows);
  });
}

export async function transferMoney(uid: string, input: { fromAccountId: string; toAccountId: string; amount: number; date: string; note?: string }) {
  const amount = Number(input.amount || 0);
  const dateISO = normalizeDateISO(input.date);
  if (!input.fromAccountId || !input.toAccountId) throw new Error('Both accounts are required.');
  if (input.fromAccountId === input.toAccountId) throw new Error('From and To account cannot be same.');
  if (amount <= 0) throw new Error('Transfer amount must be greater than zero.');
  const transferId = doc(accountLedgerCol(uid)).id;
  await runTransaction(db, async (tx) => {
    await assertFinancialDateMutableInTransaction(tx, uid, dateISO);
    const fromRef = doc(accountsCol(uid), input.fromAccountId);
    const toRef = doc(accountsCol(uid), input.toAccountId);
    const from = activeAccountFromSnap(await tx.get(fromRef));
    const to = activeAccountFromSnap(await tx.get(toRef));
    const nextFromBalance = from.currentBalance - amount;
    const nextToBalance = to.currentBalance + amount;
    tx.update(fromRef, {
      currentBalance: nextFromBalance,
      ...(from.type === 'credit_card' ? { availableLimit: Math.max(Number(from.creditLimit || 0) - nextFromBalance, 0) } : {}),
      updatedAt: serverTimestamp()
    });
    tx.update(toRef, {
      currentBalance: nextToBalance,
      ...(to.type === 'credit_card' ? { availableLimit: Math.max(Number(to.creditLimit || 0) - nextToBalance, 0) } : {}),
      updatedAt: serverTimestamp()
    });
    const now = nowISO();
    const common = { uid, amount, date: dateISO, dateISO, monthKey: monthKey(dateISO), note: input.note || `Transfer ${from.name} to ${to.name}`, transferId, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedAtISO: now };
    tx.set(doc(accountLedgerCol(uid)), { ...common, accountId: from.id, relatedAccountId: to.id, type: 'transfer_out', direction: 'debit' } satisfies Omit<AccountLedgerEntry, 'id'>);
    tx.set(doc(accountLedgerCol(uid)), { ...common, accountId: to.id, relatedAccountId: from.id, type: 'transfer_in', direction: 'credit' } satisfies Omit<AccountLedgerEntry, 'id'>);
  });
}

export async function payCreditCard(uid: string, input: { fromAccountId: string; cardAccountId: string; amount: number; date: string; note?: string }) {
  const amount = Number(input.amount || 0);
  const dateISO = normalizeDateISO(input.date);
  if (!input.fromAccountId || !input.cardAccountId) throw new Error('Payment source and card are required.');
  if (input.fromAccountId === input.cardAccountId) throw new Error('Source and card cannot be same.');
  if (amount <= 0) throw new Error('Payment amount must be greater than zero.');
  const cardPaymentId = doc(accountLedgerCol(uid)).id;
  await runTransaction(db, async (tx) => {
    await assertFinancialDateMutableInTransaction(tx, uid, dateISO);
    const fromRef = doc(accountsCol(uid), input.fromAccountId);
    const cardRef = doc(accountsCol(uid), input.cardAccountId);
    const from = activeAccountFromSnap(await tx.get(fromRef));
    const card = activeAccountFromSnap(await tx.get(cardRef));
    if (from.type === 'credit_card') throw new Error('Source account cannot be a credit card.');
    if (card.type !== 'credit_card') throw new Error('Destination must be a credit card.');
    if (amount > card.currentBalance) throw new Error('Payment is higher than current outstanding. Overpayment support is planned for a future phase.');
    const nextOutstanding = Math.max(card.currentBalance - amount, 0);
    tx.update(fromRef, { currentBalance: from.currentBalance - amount, updatedAt: serverTimestamp() });
    tx.update(cardRef, { currentBalance: nextOutstanding, availableLimit: Math.max(Number(card.creditLimit || 0) - nextOutstanding, 0), updatedAt: serverTimestamp() });
    const now = nowISO();
    const common = { uid, amount, date: dateISO, dateISO, monthKey: monthKey(dateISO), note: input.note || `Credit card payment to ${card.name}`, cardPaymentId, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), updatedAtISO: now };
    tx.set(doc(accountLedgerCol(uid)), { ...common, accountId: from.id, relatedAccountId: card.id, type: 'credit_card_payment', direction: 'debit' } satisfies Omit<AccountLedgerEntry, 'id'>);
    tx.set(doc(accountLedgerCol(uid)), { ...common, accountId: card.id, relatedAccountId: from.id, type: 'credit_card_payment', direction: 'credit' } satisfies Omit<AccountLedgerEntry, 'id'>);
  });
}

const linkedLedgerRows = async (uid: string, field: 'transferId' | 'cardPaymentId', value: string) => {
  if (!value) throw new Error('Linked event id is required.');
  const snap = await getDocs(query(accountLedgerCol(uid), where(field, '==', value), limit(5)));
  return snap.docs.map((item) => ({ ref: item.ref, row: normalizeAccountLedger({ id: item.id, ...item.data() } as AccountLedgerEntry) }));
};

const requireTransferPair = (rows: Awaited<ReturnType<typeof linkedLedgerRows>>) => {
  if (rows.length !== 2) throw new Error('Transfer linkage is incomplete. This transfer needs manual review before editing.');
  const debit = rows.find((item) => item.row.type === 'transfer_out' && item.row.direction === 'debit');
  const credit = rows.find((item) => item.row.type === 'transfer_in' && item.row.direction === 'credit');
  if (!debit || !credit) throw new Error('Transfer linkage is invalid. This transfer needs manual review before editing.');
  if (!debit.row.transferId || debit.row.transferId !== credit.row.transferId) throw new Error('Transfer linkage is broken.');
  if (debit.row.relatedAccountId !== credit.row.accountId || credit.row.relatedAccountId !== debit.row.accountId) throw new Error('Transfer account linkage is inconsistent.');
  if (Number(debit.row.amount || 0) !== Number(credit.row.amount || 0)) throw new Error('Transfer amounts are inconsistent.');
  return { debit, credit };
};

const requireCardPaymentPair = (rows: Awaited<ReturnType<typeof linkedLedgerRows>>) => {
  if (rows.length !== 2) throw new Error('Credit card payment linkage is incomplete. This payment needs manual review before editing.');
  const debit = rows.find((item) => item.row.type === 'credit_card_payment' && item.row.direction === 'debit');
  const credit = rows.find((item) => item.row.type === 'credit_card_payment' && item.row.direction === 'credit');
  if (!debit || !credit) throw new Error('Credit card payment linkage is invalid. This payment needs manual review before editing.');
  if (!debit.row.cardPaymentId || debit.row.cardPaymentId !== credit.row.cardPaymentId) throw new Error('Credit card payment linkage is broken.');
  if (debit.row.relatedAccountId !== credit.row.accountId || credit.row.relatedAccountId !== debit.row.accountId) throw new Error('Credit card payment account linkage is inconsistent.');
  if (Number(debit.row.amount || 0) !== Number(credit.row.amount || 0)) throw new Error('Credit card payment amounts are inconsistent.');
  return { debit, credit };
};

export async function updateTransfer(uid: string, transferId: string, patch: TransferEditPatch) {
  const pairRows = await linkedLedgerRows(uid, 'transferId', transferId);
  const pair = requireTransferPair(pairRows);
  await runTransaction(db, async (tx) => {
    const freshDebit = normalizeAccountLedger({ id: pair.debit.ref.id, ...(await tx.get(pair.debit.ref)).data() } as AccountLedgerEntry);
    const freshCredit = normalizeAccountLedger({ id: pair.credit.ref.id, ...(await tx.get(pair.credit.ref)).data() } as AccountLedgerEntry);
    const freshPair = requireTransferPair([{ ref: pair.debit.ref, row: freshDebit }, { ref: pair.credit.ref, row: freshCredit }]);
    assertFreshLedgerRows([freshPair.debit.row, freshPair.credit.row], patch.expectedUpdatedAtISO);
    const next = normalizeTransferEdit(freshPair.debit.row, freshPair.credit.row, patch);
    await assertFinancialMutationMutableInTransaction(tx, uid, freshPair.debit.row.dateISO || freshPair.debit.row.date, next.dateISO);
    const deltas = transferAccountDeltas(freshPair.debit.row, freshPair.credit.row, next);
    const accountIds = Array.from(new Set([freshPair.debit.row.accountId, freshPair.credit.row.accountId, next.fromAccountId, next.toAccountId]));
    const accounts = new Map<string, Account>();
    for (const accountId of accountIds) {
      const account = activeAccountFromSnap(await tx.get(doc(accountsCol(uid), accountId)));
      accounts.set(account.id, account);
    }
    for (const delta of deltas) {
      const account = accounts.get(delta.accountId);
      if (!account) throw new Error('Linked transfer account was not found.');
      accounts.set(account.id, applyLedgerDelta(account, delta.amount));
    }
    accounts.forEach((account) => updateAccountBalance(tx, account));
    const now = nowISO();
    const common = {
      amount: next.amount,
      date: next.dateISO,
      dateISO: next.dateISO,
      monthKey: next.monthKey,
      note: next.note || `Transfer ${accounts.get(next.fromAccountId)?.name || 'source'} to ${accounts.get(next.toAccountId)?.name || 'destination'}`,
      editedAt: serverTimestamp(),
      editedAtISO: now,
      mutationSource: 'transfer_edit',
      updatedAt: serverTimestamp(),
      updatedAtISO: now
    };
    tx.set(freshPair.debit.ref, { ...common, accountId: next.fromAccountId, relatedAccountId: next.toAccountId, type: 'transfer_out', direction: 'debit', editAuditLast: buildLedgerEditAudit(freshPair.debit.row, 'transfer_edit') }, { merge: true });
    tx.set(freshPair.credit.ref, { ...common, accountId: next.toAccountId, relatedAccountId: next.fromAccountId, type: 'transfer_in', direction: 'credit', editAuditLast: buildLedgerEditAudit(freshPair.credit.row, 'transfer_edit') }, { merge: true });
  });
}

export async function updateCreditCardPayment(uid: string, cardPaymentId: string, patch: CardPaymentEditPatch) {
  const pairRows = await linkedLedgerRows(uid, 'cardPaymentId', cardPaymentId);
  const pair = requireCardPaymentPair(pairRows);
  await runTransaction(db, async (tx) => {
    const freshDebit = normalizeAccountLedger({ id: pair.debit.ref.id, ...(await tx.get(pair.debit.ref)).data() } as AccountLedgerEntry);
    const freshCredit = normalizeAccountLedger({ id: pair.credit.ref.id, ...(await tx.get(pair.credit.ref)).data() } as AccountLedgerEntry);
    const freshPair = requireCardPaymentPair([{ ref: pair.debit.ref, row: freshDebit }, { ref: pair.credit.ref, row: freshCredit }]);
    assertFreshLedgerRows([freshPair.debit.row, freshPair.credit.row], patch.expectedUpdatedAtISO);
    const next = normalizeCardPaymentEdit(freshPair.debit.row, freshPair.credit.row, patch);
    await assertFinancialMutationMutableInTransaction(tx, uid, freshPair.debit.row.dateISO || freshPair.debit.row.date, next.dateISO);
    const deltas = cardPaymentAccountDeltas(freshPair.debit.row, freshPair.credit.row, next);
    const accountIds = Array.from(new Set([freshPair.debit.row.accountId, freshPair.credit.row.accountId, next.fromAccountId, next.cardAccountId]));
    const accounts = new Map<string, Account>();
    for (const accountId of accountIds) {
      const account = activeAccountFromSnap(await tx.get(doc(accountsCol(uid), accountId)));
      accounts.set(account.id, account);
    }
    const source = accounts.get(next.fromAccountId);
    const card = accounts.get(next.cardAccountId);
    if (!source || source.type === 'credit_card') throw new Error('Payment source must be a bank, cash, or wallet account.');
    if (!card || card.type !== 'credit_card') throw new Error('Destination must be a credit card.');
    const oldCardRestored = next.cardAccountId === freshPair.credit.row.accountId
      ? Number(card.currentBalance || 0) + Number(freshPair.credit.row.amount || 0)
      : Number(card.currentBalance || 0);
    if (next.amount > oldCardRestored) throw new Error('Payment is higher than current outstanding. Overpayment support is planned for a future phase.');
    for (const delta of deltas) {
      const account = accounts.get(delta.accountId);
      if (!account) throw new Error('Linked card payment account was not found.');
      accounts.set(account.id, applyLedgerDelta(account, delta.amount));
    }
    accounts.forEach((account) => updateAccountBalance(tx, account));
    const now = nowISO();
    const common = {
      amount: next.amount,
      date: next.dateISO,
      dateISO: next.dateISO,
      monthKey: next.monthKey,
      note: next.note || `Credit card payment to ${accounts.get(next.cardAccountId)?.name || 'card'}`,
      editedAt: serverTimestamp(),
      editedAtISO: now,
      mutationSource: 'card_payment_edit',
      updatedAt: serverTimestamp(),
      updatedAtISO: now
    };
    tx.set(freshPair.debit.ref, { ...common, accountId: next.fromAccountId, relatedAccountId: next.cardAccountId, type: 'credit_card_payment', direction: 'debit', editAuditLast: buildLedgerEditAudit(freshPair.debit.row, 'card_payment_edit') }, { merge: true });
    tx.set(freshPair.credit.ref, { ...common, accountId: next.cardAccountId, relatedAccountId: next.fromAccountId, type: 'credit_card_payment', direction: 'credit', editAuditLast: buildLedgerEditAudit(freshPair.credit.row, 'card_payment_edit') }, { merge: true });
  });
}

export async function saveBalanceAudit(uid: string, input: { accountId: string; actualBalance: number; actualAvailableLimit?: number; date: string; note?: string; systemBalance?: number; systemAvailableLimit?: number }) {
  const dateISO = normalizeDateISO(input.date);
  const accountRef = doc(accountsCol(uid), input.accountId);
  const snap = await getDocs(accountsCol(uid));
  const account = snap.docs.map(withId<Account>).map(normalizeAccount).find((row) => row.id === input.accountId);
  if (!account || account.status !== 'active' || account.active === false) throw new Error('Selected account is not active.');
  const actualBalance = Number(input.actualBalance || 0);
  const actualAvailableLimit = Number(input.actualAvailableLimit || 0);
  const systemBalance = Number(input.systemBalance ?? account.currentBalance);
  const systemAvailableLimit = Number(input.systemAvailableLimit ?? account.availableLimit ?? 0);
  const payload = {
    uid,
    accountId: account.id,
    auditDate: dateISO,
    dateISO,
    monthKey: monthKey(dateISO),
    accountName: account.name,
    accountType: account.type,
    systemBalance,
    actualBalance,
    difference: actualBalance - systemBalance,
    creditLimit: account.creditLimit || 0,
    systemAvailableLimit,
    actualAvailableLimit: account.type === 'credit_card' ? actualAvailableLimit : 0,
    availableLimitDifference: account.type === 'credit_card' ? actualAvailableLimit - systemAvailableLimit : 0,
    note: input.note || '',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  } satisfies Omit<BalanceAudit, 'id'>;
  debugAccounts('saveBalanceAudit', { uid, collection: `users/${uid}/balanceAudits`, accountId: input.accountId, month: payload.monthKey });
  await addDoc(balanceAuditsCol(uid), payload);
  return accountRef.id;
}

export function listenNotifications(uid: string, cb: (items: AppNotification[]) => void) {
  return onSnapshot(query(notificationsCol(uid), limit(100)), (snap) => {
    const rows = snap.docs.map(withId<AppNotification>).sort((a, b) => String(b.createdAtISO).localeCompare(String(a.createdAtISO)));
    cb(rows);
  });
}

export async function upsertNotification(uid: string, id: string, input: Omit<AppNotification, 'id' | 'uid'>) {
  const priority = input.priority === 'medium' ? 'normal' : input.priority;
  return setDoc(doc(notificationsCol(uid), id), { ...input, priority, dedupeKey: input.dedupeKey || id, uid, updatedAt: serverTimestamp(), updatedAtISO: nowISO() }, { merge: true });
}

export async function markNotificationRead(uid: string, id: string) {
  return setDoc(doc(notificationsCol(uid), id), { uid, dedupeKey: id, status: 'read', readAt: serverTimestamp(), readAtISO: nowISO(), updatedAt: serverTimestamp(), updatedAtISO: nowISO() }, { merge: true });
}

export async function dismissNotification(uid: string, id: string, priority?: AppNotification['priority']) {
  if (priority === 'critical') throw new Error('Critical alerts stay visible until the source issue is fixed.');
  return setDoc(doc(notificationsCol(uid), id), { uid, dedupeKey: id, status: 'dismissed', dismissedAt: serverTimestamp(), dismissedAtISO: nowISO(), updatedAt: serverTimestamp(), updatedAtISO: nowISO() }, { merge: true });
}

export async function markNotificationKeysRead(uid: string, ids: string[]) {
  const unique = Array.from(new Set(ids.filter(Boolean))).slice(0, 100);
  await Promise.all(unique.map((id) => markNotificationRead(uid, id)));
}

export async function markAllNotificationsRead(uid: string) {
  const snap = await getDocs(query(notificationsCol(uid), where('status', '==', 'unread'), limit(100)));
  await Promise.all(snap.docs.map((item) => updateDoc(item.ref, { status: 'read', readAt: serverTimestamp(), readAtISO: nowISO() })));
}

export function listenNotificationPreferences(uid: string, emailAddress: string, cb: (prefs: NotificationPreferences) => void) {
  return onSnapshot(notificationSettingsDoc(uid), (snap) => {
    cb(normalizeNotificationPreferences(snap.exists() ? { id: snap.id, ...snap.data() } as Partial<NotificationPreferences> : undefined, emailAddress));
  });
}

export async function saveNotificationPreferences(uid: string, input: Partial<NotificationPreferences>, emailAddress = '') {
  const existing = await getDoc(notificationSettingsDoc(uid));
  const prefs = normalizeNotificationPreferences({ ...(existing.exists() ? existing.data() as Partial<NotificationPreferences> : {}), ...input }, emailAddress);
  return setDoc(notificationSettingsDoc(uid), {
    ...prefs,
    id: 'notifications',
    uid,
    updatedAt: serverTimestamp(),
    updatedAtISO: nowISO(),
    createdAt: existing.exists() ? existing.data().createdAt : serverTimestamp()
  }, { merge: true });
}

export function listenNotificationDevices(uid: string, cb: (devices: NotificationDevice[]) => void) {
  return onSnapshot(query(notificationDevicesCol(uid), limit(20)), (snap) => {
    cb(snap.docs.map(withId<NotificationDevice>).sort((a, b) => Number(b.enabled) - Number(a.enabled) || String(b.lastSeenAt || '').localeCompare(String(a.lastSeenAt || ''))));
  });
}

export async function saveNotificationDevice(uid: string, deviceId: string, input: Pick<NotificationDevice, 'token' | 'platform' | 'userAgentSummary'> & Partial<NotificationDevice>) {
  const tokenHash = Array.from(input.token).reduce((hash, char) => ((hash << 5) - hash + char.charCodeAt(0)) | 0, 0).toString(16);
  const ref = doc(notificationDevicesCol(uid), deviceId);
  const existing = await getDoc(ref);
  return setDoc(ref, {
    ...input,
    id: deviceId,
    uid,
    platform: 'web',
    enabled: true,
    tokenHash,
    tokenVersion: 'fcm-web-v1',
    createdAt: existing.exists() ? existing.data().createdAt : serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastSeenAt: serverTimestamp()
  }, { merge: true });
}

export async function disableNotificationDevice(uid: string, deviceId: string) {
  return setDoc(doc(notificationDevicesCol(uid), deviceId), { uid, enabled: false, disabledAt: serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
}

export function listenNotificationDeliveries(uid: string, cb: (deliveries: NotificationDelivery[]) => void) {
  return onSnapshot(query(notificationDeliveriesCol(uid), limit(50)), (snap) => cb(snap.docs.map(withId<NotificationDelivery>)));
}

export async function recordNotificationDelivery(uid: string, id: string, input: Omit<NotificationDelivery, 'id' | 'uid' | 'createdAt' | 'updatedAt'>) {
  const ref = doc(notificationDeliveriesCol(uid), id);
  const existing = await getDoc(ref);
  const previous = existing.exists() ? existing.data() as Partial<NotificationDelivery> : {};
  return setDoc(ref, {
    ...input,
    uid,
    attemptCount: Number(previous.attemptCount || 0) + 1,
    createdAt: previous.createdAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastAttemptAt: serverTimestamp()
  }, { merge: true });
}

export async function uploadReceipt(uid: string, file: File) {
  const path = `users/${uid}/receipts/${Date.now()}-${file.name}`;
  const fileRef = ref(storage, path);
  await uploadBytes(fileRef, file);
  return { attachmentUrl: await getDownloadURL(fileRef), attachmentPath: path };
}

export async function saveTransaction(uid: string, input: Partial<Transaction>, file?: File) {
  const dateISO = normalizeDateISO(input.dateISO || input.date);
  const period = await fetchMonthlyPeriod(uid, resolveFinancialMonthKey(dateISO));
  assertFinancialDateMutableStatus(dateISO, period);
  const attachment = file ? await uploadReceipt(uid, file) : {};
  const accountId = input.accountId || '';
  const payload = {
    uid,
    date: dateISO,
    dateISO,
    displayDate: displayDate(dateISO),
    monthKey: monthKey(dateISO),
    type: input.type || 'Expense',
    category: input.category || defaultDropdownValue(DROPDOWN_GROUPS.DAILY_CATEGORY),
    amount: Number(input.amount || 0),
    paymentMode: input.paymentMode || defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE),
    accountId,
    accountName: input.accountName || '',
    accountType: input.accountType || '',
    crdrPerson: input.crdrPerson || '',
    taxTag: input.taxTag || '',
    taxCategory: input.taxCategory || '',
    reportGroup: input.reportGroup || '',
    isTaxRelevant: input.isTaxRelevant ?? Boolean(input.taxTag && input.taxTag !== 'Ignore for ITR'),
    note: input.note || '',
    source: input.source || 'Manual',
    refId: input.refId || '',
    attachmentUrl: input.attachmentUrl || (attachment as any).attachmentUrl || '',
    attachmentPath: input.attachmentPath || (attachment as any).attachmentPath || '',
    createdAt: input.createdAt || serverTimestamp(),
    createdAtISO: input.createdAt || nowISO(),
    updatedAt: serverTimestamp(),
    updatedAtISO: nowISO()
  };
  debugFirestore('saveTransaction', { uid, path: `users/${uid}/transactions`, monthKey: payload.monthKey, type: payload.type, hasAmount: payload.amount > 0 });
  if (input.id) throw new Error('Use safe transaction editing for existing transactions.');
  if (accountId) {
    const txRef = doc(transactionsCol(uid));
    const accountRef = doc(accountsCol(uid), accountId);
    const ledgerRef = doc(accountLedgerCol(uid));
    return runTransaction(db, async (firestoreTx) => {
      await assertFinancialDateMutableInTransaction(firestoreTx, uid, dateISO);
      const accountSnap = await firestoreTx.get(accountRef);
      if (!accountSnap.exists()) throw new Error('Selected account was not found.');
      const account = normalizeAccount({ id: accountSnap.id, ...accountSnap.data() } as Account);
      if (account.status !== 'active' || account.active === false) throw new Error('Selected account is not active.');
      const amount = Number(payload.amount || 0);
      const isCreditCard = account.type === 'credit_card';
      const isIncome = payload.type === 'Income';
      const ledgerType: AccountLedgerEntry['type'] = isCreditCard && payload.type === 'Expense' ? 'credit_card_spend' : payload.type.toLowerCase() as AccountLedgerEntry['type'];
      const direction: AccountLedgerEntry['direction'] = isIncome ? 'credit' : 'debit';
      const nextBalance = isCreditCard
        ? account.currentBalance + (payload.type === 'Expense' ? amount : 0)
        : account.currentBalance + (isIncome ? amount : -amount);
      const nextAvailable = isCreditCard ? Math.max(Number(account.creditLimit || 0) - nextBalance, 0) : 0;
      firestoreTx.set(txRef, { ...payload, accountId: account.id, accountName: account.name, accountType: account.type });
      firestoreTx.update(accountRef, {
        currentBalance: nextBalance,
        ...(isCreditCard ? { availableLimit: nextAvailable } : {}),
        updatedAt: serverTimestamp()
      });
      firestoreTx.set(ledgerRef, {
        uid,
        accountId: account.id,
        transactionId: txRef.id,
        type: ledgerType,
        amount,
        direction,
        date: dateISO,
        dateISO,
        monthKey: payload.monthKey,
        note: payload.note || `${payload.type}: ${payload.category}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      } satisfies Omit<AccountLedgerEntry, 'id'>);
    });
  }
  const txRef = doc(transactionsCol(uid));
  await runTransaction(db, async (firestoreTx) => {
    await assertFinancialDateMutableInTransaction(firestoreTx, uid, dateISO);
    firestoreTx.set(txRef, payload);
  });
  return txRef;
}

const transactionLedgerRows = async (uid: string, transactionId: string) => {
  const snap = await getDocs(query(accountLedgerCol(uid), where('transactionId', '==', transactionId), limit(5)));
  return snap.docs.map((item) => ({ ref: item.ref, row: normalizeAccountLedger({ id: item.id, ...item.data() } as AccountLedgerEntry) }));
};

const requireSingleTransactionLedger = (rows: Awaited<ReturnType<typeof transactionLedgerRows>>, original: Transaction) => {
  if (!original.accountId) return rows[0] || null;
  if (rows.length !== 1) throw new Error('Linked account ledger is missing or duplicated. This transaction needs manual review before editing.');
  return rows[0];
};

export async function updateFinancialTransaction(uid: string, transactionId: string, patch: EditableTransactionPatch) {
  const ledgerRows = await transactionLedgerRows(uid, transactionId);
  const txRef = doc(transactionsCol(uid), transactionId);
  await runTransaction(db, async (firestoreTx) => {
    const txSnap = await firestoreTx.get(txRef);
    if (!txSnap.exists()) throw new Error('Transaction was not found.');
    const original = normalizeTransaction({ id: txSnap.id, ...txSnap.data() } as Transaction);
    const eligibility = canEditTransaction(original);
    if (!eligibility.canEdit) throw new Error(eligibility.reason);
    assertFreshTransactionVersion(original, patch.expectedUpdatedAtISO);

    const originalLedger = requireSingleTransactionLedger(ledgerRows, original);
    const oldImpact = financialImpact(original);
    const next = normalizeEditableTransaction(original, patch);
    await assertFinancialMutationMutableInTransaction(firestoreTx, uid, original.dateISO || original.date, next.dateISO);
    const newAccountRef = next.accountId ? doc(accountsCol(uid), next.accountId) : null;
    const oldAccountRef = oldImpact ? doc(accountsCol(uid), oldImpact.accountId) : null;
    let oldAccount: Account | null = null;
    let newAccount: Account | null = null;

    if (oldAccountRef) oldAccount = activeAccountFromSnap(await firestoreTx.get(oldAccountRef));
    if (newAccountRef && (!oldAccount || newAccountRef.path !== oldAccountRef?.path)) newAccount = activeAccountFromSnap(await firestoreTx.get(newAccountRef));
    if (newAccountRef && oldAccount && newAccountRef.path === oldAccountRef?.path) newAccount = oldAccount;

    if (newAccount) {
      next.accountId = newAccount.id;
      next.accountName = newAccount.name;
      next.accountType = newAccount.type;
    } else {
      next.accountId = '';
      next.accountName = '';
      next.accountType = undefined;
    }

    const newImpact = financialImpact(next);
    if (oldAccount) updateAccountImpact(firestoreTx, oldAccount, reverseImpact(oldImpact));
    if (newAccount) {
      const base = oldAccount && oldAccount.id === newAccount.id && oldImpact
        ? { ...newAccount, currentBalance: Number(newAccount.currentBalance || 0) - oldImpact.balanceDelta }
        : newAccount;
      updateAccountImpact(firestoreTx, base, newImpact);
    }

    const now = nowISO();
    firestoreTx.update(txRef, {
      ...next,
      editAuditLast: buildTransactionEditAudit(original),
      editedAt: serverTimestamp(),
      editedAtISO: now,
      mutationSource: 'transaction_edit',
      updatedAt: serverTimestamp(),
      updatedAtISO: now
    });

    if (oldImpact || newImpact) {
      const ledgerRef = originalLedger?.ref || doc(accountLedgerCol(uid));
      const ledgerPayload = newImpact ? {
        uid,
        accountId: newImpact.accountId,
        transactionId,
        type: newImpact.ledgerType,
        amount: newImpact.amount,
        direction: newImpact.direction,
        date: next.dateISO,
        dateISO: next.dateISO,
        monthKey: next.monthKey,
        note: next.note || `${next.type}: ${next.category}`,
        createdAt: originalLedger?.row.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp()
      } satisfies Omit<AccountLedgerEntry, 'id'> : {
        uid,
        accountId: oldImpact?.accountId || original.accountId || '',
        transactionId,
        type: oldImpact?.ledgerType || originalLedger?.row.type || 'expense',
        amount: 0,
        direction: oldImpact?.direction || originalLedger?.row.direction || 'debit',
        date: next.dateISO,
        dateISO: next.dateISO,
        monthKey: next.monthKey,
        note: `Account link removed by transaction edit: ${next.note || next.category}`,
        createdAt: originalLedger?.row.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp()
      } satisfies Omit<AccountLedgerEntry, 'id'>;
      firestoreTx.set(ledgerRef, ledgerPayload, { merge: true });
    }
  });
}

export async function deleteTransaction(uid: string, id: string) {
  const ledgerRows = await transactionLedgerRows(uid, id);
  const txRef = doc(transactionsCol(uid), id);
  await runTransaction(db, async (firestoreTx) => {
    const txSnap = await firestoreTx.get(txRef);
    if (!txSnap.exists()) throw new Error('Transaction was not found.');
    const original = normalizeTransaction({ id: txSnap.id, ...txSnap.data() } as Transaction);
    if (original.status === 'deleted' || original.active === false) return;
    const eligibility = canEditTransaction(original);
    if (!eligibility.canEdit) throw new Error(eligibility.reason);
    await assertFinancialDateMutableInTransaction(firestoreTx, uid, original.dateISO || original.date);
    const originalLedger = requireSingleTransactionLedger(ledgerRows, original);
    const oldImpact = financialImpact(original);
    if (oldImpact) {
      const accountRef = doc(accountsCol(uid), oldImpact.accountId);
      const account = activeAccountFromSnap(await firestoreTx.get(accountRef));
      updateAccountImpact(firestoreTx, account, reverseImpact(oldImpact));
      if (originalLedger) {
        firestoreTx.set(originalLedger.ref, {
          amount: 0,
          note: `Reversed by transaction delete: ${original.note || original.category}`,
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
    }
    const now = nowISO();
    firestoreTx.update(txRef, {
      status: 'deleted',
      active: false,
      deletedAt: serverTimestamp(),
      deletedAtISO: now,
      editAuditLast: buildTransactionEditAudit(original, 'transaction_delete'),
      mutationSource: 'transaction_delete',
      updatedAt: serverTimestamp(),
      updatedAtISO: now
    });
  });
}

export function listenMonthTransactions(uid: string, key: string, cb: (items: Transaction[]) => void) {
  const path = `users/${uid}/transactions`;
  return onSnapshot(query(transactionsCol(uid), limit(500)), (snap) => {
    const rows = snap.docs
      .map(withId<Transaction>)
      .map(normalizeTransaction)
      .filter((row) => row.status !== 'deleted' && row.active !== false)
      .filter((row) => row.monthKey === key)
      .sort((a, b) => b.dateISO.localeCompare(a.dateISO));
    debugFirestore('listenMonthTransactions', { uid, path, queryMonthKey: key, fetched: snap.size, matched: rows.length });
    cb(rows);
  });
}

export async function fetchRecordsPage(uid: string, key: string | '', pageSize: number, cursor?: QueryDocumentSnapshot<DocumentData>) {
  const constraints = [limit(key ? 500 : pageSize)];
  const q = cursor && !key ? query(transactionsCol(uid), ...constraints, startAfter(cursor)) : query(transactionsCol(uid), ...constraints);
  const snap = await getDocs(q);
  const rows = snap.docs
    .map(withId<Transaction>)
    .map(normalizeTransaction)
    .filter((row) => row.status !== 'deleted' && row.active !== false)
    .filter((row) => !key || row.monthKey === key)
    .sort((a, b) => b.dateISO.localeCompare(a.dateISO))
    .slice(0, pageSize);
  debugFirestore('fetchRecordsPage', { uid, path: `users/${uid}/transactions`, queryMonthKey: key || '(all)', fetched: snap.size, matched: rows.length });
  return { rows, cursor: snap.docs.at(-1) };
}

export async function fetchPaymentsPage(uid: string, key: string | '', pageSize = 60) {
  const snap = await getDocs(query(paymentsCol(uid), limit(key ? 500 : pageSize)));
  const rows = snap.docs
    .map(withId<PlanPayment>)
    .map(normalizePlanPayment)
    .filter((row) => !key || row.monthKey === key)
    .sort((a, b) => b.paidDate.localeCompare(a.paidDate))
    .slice(0, pageSize);
  debugFirestore('fetchPaymentsPage', { uid, path: `users/${uid}/planPayments`, queryMonthKey: key || '(all)', fetched: snap.size, matched: rows.length });
  return rows;
}

export async function fetchPlansPage(uid: string, pageSize = 60) {
  const snap = await getDocs(query(plansCol(uid), orderBy('dueDate', 'asc'), limit(pageSize)));
  return snap.docs.map(withId<Plan>).map(normalizePlan).filter((plan) => plan.status !== 'deleted');
}

export async function fetchCrDrPage(uid: string, key: string | '', pageSize = 60) {
  const snap = await getDocs(query(crdrCol(uid), limit(key ? 500 : pageSize)));
  const rows = snap.docs
    .map(withId<CrDrEntry>)
    .map(normalizeCrDr)
    .filter((row) => !key || row.monthKey === key)
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, pageSize);
  debugFirestore('fetchCrDrPage', { uid, path: `users/${uid}/crdr`, queryMonthKey: key || '(all)', fetched: snap.size, matched: rows.length });
  return rows;
}

export async function fetchMonthlyReportData(uid: string, key: string) {
  const data = await fetchReportData(uid);
  return {
    ...data,
    transactions: data.transactions.filter((row) => row.monthKey === key),
    payments: data.payments.filter((row) => row.monthKey === key),
    plans: data.plans,
    crdr: data.crdr.filter((row) => row.monthKey === key),
    budgets: data.budgets.filter((row) => row.monthKey === key),
    accountLedger: data.accountLedger.filter((row) => row.monthKey === key),
    balanceAudits: data.balanceAudits.filter((row) => row.monthKey === key)
  };
}

export async function fetchReportData(uid: string) {
  const [txSnap, paymentSnap, planSnap, crdrSnap, budgetSnap, accountSnap, accountLedgerSnap, auditSnap, periodSnap] = await Promise.all([
    getDocs(transactionsCol(uid)),
    getDocs(paymentsCol(uid)),
    getDocs(plansCol(uid)),
    getDocs(crdrCol(uid)),
    getDocs(budgetsCol(uid)),
    getDocs(accountsCol(uid)),
    getDocs(accountLedgerCol(uid)),
    getDocs(balanceAuditsCol(uid)),
    getDocs(monthlyPeriodsCol(uid))
  ]);
  debugReports('fetch transactions', { uid, collection: `users/${uid}/transactions`, fetched: txSnap.size, month: '(client-range)' });
  debugReports('fetch planPayments', { uid, collection: `users/${uid}/planPayments`, fetched: paymentSnap.size, month: '(client-range)' });
  debugReports('fetch plans', { uid, collection: `users/${uid}/plans`, fetched: planSnap.size, month: '(client-range)' });
  debugReports('fetch crdr', { uid, collection: `users/${uid}/crdr`, fetched: crdrSnap.size, month: '(client-range)' });
  debugReports('fetch budgets', { uid, collection: `users/${uid}/budgets`, fetched: budgetSnap.size, month: '(client-range)' });
  debugReports('fetch accounts', { uid, collection: `users/${uid}/accounts`, fetched: accountSnap.size, month: '(client-range)' });
  debugReports('fetch accountLedger', { uid, collection: `users/${uid}/accountLedger`, fetched: accountLedgerSnap.size, month: '(client-range)' });
  debugReports('fetch balanceAudits', { uid, collection: `users/${uid}/balanceAudits`, fetched: auditSnap.size, month: '(client-range)' });
  debugReports('fetch monthlyPeriods', { uid, collection: `users/${uid}/monthlyPeriods`, fetched: periodSnap.size, month: '(client-range)' });

  const transactions = txSnap.docs
    .map(withId<Transaction>)
    .map(normalizeTransaction)
    .filter((row) => row.status !== 'deleted' && row.active !== false)
    .sort((a, b) => b.dateISO.localeCompare(a.dateISO));
  const payments = paymentSnap.docs
    .map(withId<PlanPayment>)
    .map(normalizePlanPayment)
    .sort((a, b) => b.paidDate.localeCompare(a.paidDate));
  const plans = planSnap.docs
    .map(withId<Plan>)
    .map(normalizePlan)
    .filter((plan) => plan.status !== 'deleted' && plan.active !== false)
    .sort((a, b) => (a.nextDueDate || a.dueDate).localeCompare(b.nextDueDate || b.dueDate));
  const crdr = crdrSnap.docs
    .map(withId<CrDrEntry>)
    .map(normalizeCrDr)
    .sort((a, b) => b.date.localeCompare(a.date));
  const budgets = budgetSnap.docs
    .map(withId<Budget>)
    .map(normalizeBudget)
    .filter((row) => row.status !== 'deleted' && row.active !== false)
    .sort((a, b) => a.category.localeCompare(b.category));
  const accounts = accountSnap.docs
    .map(withId<Account>)
    .map(normalizeAccount)
    .filter((row) => row.status !== 'deleted')
    .sort((a, b) => a.type.localeCompare(b.type) || a.name.localeCompare(b.name));
  const accountLedger = accountLedgerSnap.docs
    .map(withId<AccountLedgerEntry>)
    .map(normalizeAccountLedger)
    .sort((a, b) => b.dateISO.localeCompare(a.dateISO));
  const balanceAudits = auditSnap.docs
    .map(withId<BalanceAudit>)
    .map(normalizeBalanceAudit)
    .sort((a, b) => b.dateISO.localeCompare(a.dateISO));
  const monthlyPeriods = periodSnap.docs
    .map((item) => normalizeMonthlyPeriod({ id: item.id, ...item.data() }, uid, item.id))
    .sort((a, b) => b.monthKey.localeCompare(a.monthKey));

  return { transactions, payments, plans, crdr, budgets, accounts, accountLedger, balanceAudits, monthlyPeriods };
}

export async function savePlan(uid: string, input: Partial<Plan>) {
  const dueDate = normalizeDateISO(input.nextDueDate || input.dueDate);
  const status = normalizePlanStatus(String(input.status || 'active'));
  const payload = {
    uid,
    title: input.title || 'Untitled plan',
    category: input.category || defaultDropdownValue(DROPDOWN_GROUPS.PLAN_CATEGORY),
    flow: input.flow || 'Expense',
    amount: Number(input.amount || 0),
    dueDate,
    nextDueDate: dueDate,
    monthKey: monthKey(dueDate),
    frequency: (input.frequency || defaultDropdownValue(DROPDOWN_GROUPS.PLAN_FREQUENCY)) as PlanFrequency,
    paidCount: Number(input.paidCount || 0),
    totalCount: Number(input.totalCount || 0),
    status,
    active: input.active ?? status === 'active',
    paymentMode: input.paymentMode || defaultDropdownValue(DROPDOWN_GROUPS.PAYMENT_MODE),
    calendarSyncEnabled: input.calendarSyncEnabled ?? false,
    reminderDaysBefore: Number(input.reminderDaysBefore ?? 3),
    calendarEventId: input.calendarEventId || '',
    calendarLastSyncedAt: input.calendarLastSyncedAt || '',
    paidFromAccountId: input.paidFromAccountId || '',
    paidFromAccountName: input.paidFromAccountName || '',
    ...(input.paidFromAccountType ? { paidFromAccountType: input.paidFromAccountType } : {}),
    contactName: input.contactName || '',
    autoCreateDaily: input.autoCreateDaily ?? true,
    note: input.note || '',
    createdAt: input.createdAt || nowISO(),
    updatedAt: nowISO(),
    pausedAt: input.pausedAt || '',
    deletedAt: input.deletedAt || ''
  };
  if (input.id) return updateDoc(doc(plansCol(uid), input.id), payload);
  return addDoc(plansCol(uid), payload);
}

export const pausePlan = (uid: string, id: string) => updateDoc(doc(plansCol(uid), id), {
  status: 'paused',
  active: false,
  pausedAt: serverTimestamp(),
  pausedAtISO: nowISO(),
  updatedAt: serverTimestamp(),
  updatedAtISO: nowISO()
});

export const resumePlan = (uid: string, id: string, dueDate?: string) => {
  const next = dueDate ? normalizeDateISO(dueDate) : undefined;
  return updateDoc(doc(plansCol(uid), id), {
    status: 'active',
    active: true,
    ...(next ? { dueDate: next, nextDueDate: next, monthKey: monthKey(next) } : {}),
    updatedAt: serverTimestamp(),
    updatedAtISO: nowISO()
  });
};

export const deletePlan = (uid: string, id: string) => updateDoc(doc(plansCol(uid), id), {
  status: 'deleted',
  active: false,
  deletedAt: serverTimestamp(),
  deletedAtISO: nowISO(),
  updatedAt: serverTimestamp(),
  updatedAtISO: nowISO()
});

export function listenUpcomingPlans(uid: string, cb: (items: Plan[]) => void) {
  const path = `users/${uid}/plans`;
  return onSnapshot(query(plansCol(uid), limit(250)), (snap) => {
    const rows = snap.docs.map(withId<Plan>).map(normalizePlan).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    const visible = rows.filter((plan) => plan.status !== 'deleted');
    debugPlans('listenPlans', {
      uid,
      path,
      active: visible.filter((plan) => plan.status === 'active').length,
      paused: visible.filter((plan) => plan.status === 'paused').length,
      deletedHidden: rows.length - visible.length
    });
    cb(visible);
  });
}

export async function markPlanPaid(uid: string, plan: Plan, paymentMode: string, remark: string, createTransaction = true, paidFromAccountId?: string) {
  const paidDate = todayISO();
  const period = await fetchMonthlyPeriod(uid, resolveFinancialMonthKey(paidDate));
  assertFinancialDateMutableStatus(paidDate, period);
  const paymentId = `plan_paid_${plan.id}_${paidDate}`;
  const paidCreated = await runTransaction(db, async (tx) => {
    await assertFinancialDateMutableInTransaction(tx, uid, paidDate);
    const planRef = doc(plansCol(uid), plan.id);
    const paymentRef = doc(paymentsCol(uid), paymentId);
    const accountId = paidFromAccountId || plan.paidFromAccountId || '';
    const accountRef = accountId ? doc(accountsCol(uid), accountId) : null;
    const ledgerRef = accountId ? doc(accountLedgerCol(uid)) : null;
    const transactionRef = createTransaction ? doc(transactionsCol(uid)) : null;
    const paymentSnap = await tx.get(paymentRef);
    if (paymentSnap.exists()) return false;
    const planSnap = await tx.get(planRef);
    const currentPlan = planSnap.exists() ? normalizePlan({ id: plan.id, ...planSnap.data() } as Plan) : plan;
    if (currentPlan.status !== 'active' && currentPlan.status !== 'Active') return false;
    let account: Account | null = null;
    if (accountRef) {
      account = activeAccountFromSnap(await tx.get(accountRef));
      if (account.type === 'credit_card') throw new Error('Paid-from account cannot be a credit card for this plan payment.');
    }
    const next = nextDueDate(currentPlan.nextDueDate || currentPlan.dueDate, currentPlan.frequency);
    const paidCount = (currentPlan.paidCount || 0) + 1;
    const done = currentPlan.frequency === 'One Time' || (currentPlan.totalCount > 0 && paidCount >= currentPlan.totalCount);
    tx.set(paymentRef, {
      uid,
      planId: currentPlan.id,
      planName: currentPlan.title,
      title: currentPlan.title,
      category: currentPlan.category,
      flow: currentPlan.flow,
      amount: currentPlan.amount,
      paidDate,
      monthKey: monthKey(paidDate),
      paymentMode,
      paidFromAccountId: account?.id || '',
      paidFromAccountName: account?.name || '',
      paidFromAccountType: account?.type,
      note: remark,
      remark,
      createdAt: nowISO(),
      updatedAt: nowISO()
    } as Omit<PlanPayment, 'id'>);
    if (account && accountRef && ledgerRef) {
      tx.update(accountRef, { currentBalance: account.currentBalance - currentPlan.amount, updatedAt: serverTimestamp() });
      tx.set(ledgerRef, {
        uid,
        accountId: account.id,
        planId: currentPlan.id,
        planPaymentId: paymentId,
        type: 'plan_payment',
        amount: currentPlan.amount,
        direction: 'debit',
        date: paidDate,
        dateISO: paidDate,
        monthKey: monthKey(paidDate),
        note: `Plan paid: ${currentPlan.title}${remark ? ' - ' + remark : ''}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      } satisfies Omit<AccountLedgerEntry, 'id'>);
    }
    if (transactionRef) {
      const createdAtISO = nowISO();
      tx.set(transactionRef, {
        uid,
        date: paidDate,
        dateISO: paidDate,
        displayDate: displayDate(paidDate),
        monthKey: monthKey(paidDate),
        type: currentPlan.flow,
        category: currentPlan.category,
        amount: Number(currentPlan.amount || 0),
        paymentMode,
        accountId: '',
        accountName: '',
        accountType: undefined,
        crdrPerson: '',
        taxTag: '',
        taxCategory: '',
        reportGroup: '',
        isTaxRelevant: false,
        note: `Paid: ${currentPlan.title}${remark ? ' - ' + remark : ''}`,
        source: 'PlanPayment',
        refId: currentPlan.id,
        attachmentUrl: '',
        attachmentPath: '',
        createdAt: serverTimestamp(),
        createdAtISO,
        updatedAt: serverTimestamp(),
        updatedAtISO: createdAtISO
      } satisfies Omit<Transaction, 'id'>);
    }
    tx.update(planRef, { paidCount, dueDate: done ? currentPlan.dueDate : next, nextDueDate: done ? currentPlan.dueDate : next, monthKey: monthKey(done ? currentPlan.dueDate : next), status: done ? 'done' : 'active', active: !done, paymentMode, updatedAt: serverTimestamp(), updatedAtISO: nowISO() });
    return true;
  });
  if (!paidCreated) return false;
  await upsertNotification(uid, `plan_paid_${plan.id}_${paidDate}`, {
    type: 'plan_paid',
    title: 'Plan marked paid',
    message: `${plan.title} paid on ${displayDate(paidDate)} using ${paymentMode}.`,
    relatedType: 'plan',
    relatedId: plan.id,
    dueDate: paidDate,
    status: 'unread',
    priority: 'normal',
    createdAt: serverTimestamp(),
    createdAtISO: nowISO()
  });
  return true;
}

export function listenPlanPaymentHistory(uid: string, planId: string, cb: (items: PlanPayment[]) => void) {
  const path = `users/${uid}/planPayments`;
  return onSnapshot(query(paymentsCol(uid), where('planId', '==', planId), limit(100)), (snap) => {
    const rows = snap.docs.map(withId<PlanPayment>).sort((a, b) => String(b.paidDate).localeCompare(String(a.paidDate)));
    debugPlans('listenPlanPaymentHistory', { uid, path, planId, count: rows.length });
    cb(rows);
  });
}

export function listenPayments(uid: string, key: string, cb: (items: PlanPayment[]) => void) {
  return onSnapshot(query(paymentsCol(uid), where('monthKey', '==', key), orderBy('paidDate', 'desc')), (snap) => cb(snap.docs.map(withId<PlanPayment>)));
}

export async function saveCrDr(uid: string, input: Partial<CrDrEntry>) {
  const date = normalizeDateISO(input.date);
  const payload = { uid, date, monthKey: monthKey(date), name: input.name || input.contactName || '', contactId: input.contactId || '', contactName: input.contactName || input.name || '', contactPhone: input.contactPhone || '', contactEmail: input.contactEmail || '', type: input.type || defaultDropdownValue(DROPDOWN_GROUPS.CRDR_TYPE), amount: Number(input.amount || 0), note: input.note || '', status: input.status || 'Open', updatedAt: nowISO() };
  const entryRef = input.id ? doc(crdrCol(uid), input.id) : doc(crdrCol(uid));
  await runTransaction(db, async (tx) => {
    if (input.id) {
      const existingSnap = await tx.get(entryRef);
      if (!existingSnap.exists()) throw new Error('CR/DR entry was not found.');
      const original = normalizeCrDr({ id: existingSnap.id, ...existingSnap.data() } as CrDrEntry);
      await assertFinancialMutationMutableInTransaction(tx, uid, original.date, date);
      tx.update(entryRef, payload);
      return;
    }
    await assertFinancialDateMutableInTransaction(tx, uid, date);
    tx.set(entryRef, { ...payload, createdAt: nowISO() });
  });
  return entryRef;
}

export async function deleteCrDr(uid: string, id: string) {
  const entryRef = doc(crdrCol(uid), id);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(entryRef);
    if (!snap.exists()) return;
    const original = normalizeCrDr({ id: snap.id, ...snap.data() } as CrDrEntry);
    await assertFinancialDateMutableInTransaction(tx, uid, original.date);
    tx.delete(entryRef);
  });
}

export function listenMonthlySummary(uid: string, key: string, cb: (summary: MonthlySummary | null) => void) {
  return onSnapshot(doc(summariesCol(uid), key), (snap) => cb(snap.exists() ? ({ id: snap.id, ...snap.data() } as MonthlySummary) : null));
}

